import symmioAcademyImg from "../../assets/images/resources/welcome-academy.jpg"
import marketingMaterialsImg from "../../assets/images/resources/marketing-materials.jpg"
import helpCenterImg from "../../assets/images/resources/help-center.jpg"
import { useAuthState } from "../../utilities/contexts/auth-state-context";
import { useTranslation } from 'react-i18next';
import axios from "axios";
import { Loader } from "../../components/loader/loader";
import { useEffect, useState } from "react";
import { Modal } from "../../components/modal/modal";
import ErrorLogService from "../../utilities/services/error-log-service";

const COMPONENT_CLASS = "p-resources";

interface AuthTokenResponse {
    access_token: string;
    token_type: string;
    expires_in: number;
}

interface AuthResponse {
    ExternalId: string;
    Token: string;
    IsNewUser: boolean;
    Success: boolean;
    error?: string;
}

const ResourcesPage: React.FC = () => {
    const { state } = useAuthState();
    const { t } = useTranslation();

    const [isLoading, setIsLoading] = useState(false);
    const [showError, setShowError] = useState(false);

    const params = new URLSearchParams(window.location.search);
    const openAcademyStr = params.get("openAcademy");
    const openAcademy = openAcademyStr ? openAcademyStr.toLowerCase() === "true" : false;

    const sendRequestToFMS = async () => {
        if (!state.user) {
            return;
        }

        setIsLoading(true);

        try {
            const tokenUrl = process.env.REACT_APP_FMS_API_TOKEN;
            const apiUrl = process.env.REACT_APP_FMS_API_ACADEMY_AUTH;

            if (!tokenUrl || !apiUrl) {
                console.error('API URLs are not defined');
                throw new Error('API URLs are not defined');
            }

            const reqBodyAccessToken = {
                UserName: process.env.REACT_APP_FMS_API_TOKEN_USERNAME,
                Password: process.env.REACT_APP_FMS_API_TOKEN_PASSWORD,
                grant_type: "password",
                api_key: process.env.REACT_APP_FMS_API_TOKEN_KEY,
            };

            // Get Authorization Token
            const tokenResponse = await axios.post<AuthTokenResponse>(tokenUrl, reqBodyAccessToken, {
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
            });

            const accessToken = tokenResponse.data.access_token;

            // Authenticate User with Authorization Header
            const reqBody = {
                firstName: state.user.firstName || "",
                lastName: state.user.lastName || "",
                email: state.user.email || "",
                externalId: state.user.id || "",
                role: state.user.roles && state.user.roles.length > 0 ? state.user.roles[0] : "",
            };

            const authResponse = await axios.post<AuthResponse>(apiUrl, reqBody, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`,
                }
            });

            const data = authResponse.data;

            if (data.Success && data.Token) {
                const redirectBaseUrl = process.env.REACT_APP_FMS_ACADEMY_URL;

                if (!redirectBaseUrl) {
                    console.error('Redirect URL is not defined');
                    throw new Error('Redirect URL is not defined');
                }
                const planType = state.organization.plan?.value || null;

                const redirectUrl = `${redirectBaseUrl}?token=${data.Token}&memberType=${planType}`;
                window.open(redirectUrl, '_blank', 'noopener,noreferrer');
            } else {
                console.error('Authentication failed: ', data.error || 'Unknown error');
                setShowError(true);
            }
        } catch (error: unknown) {
            setShowError(true);

            if (axios.isAxiosError(error)) {
                console.error('Axios error: ', error.response?.data || error.message);
                ErrorLogService.add({
                    userId: state.user.id ?? "",
                    message: `Axios Error: ${error.response?.data || error.message}`,
                    data: [error]
                });
            } else {
                console.error('Unexpected error: ', error);
                ErrorLogService.add({
                    userId: state.user.id ?? "",
                    message: `Unexcpected Error`,
                    data: [error]
                });
            }
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (openAcademy) {
            sendRequestToFMS();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openAcademy])

    return (
        <div className={`${COMPONENT_CLASS}`}>
            <Loader isVisible={isLoading}></Loader>

            <Modal
                isOpen={showError}
                isLoading={false}
                onClose={() => {
                    setShowError(false);
                }}
                title={t('pages.resources.errorTitle')}
                defaultCenterModal={true}
                submitButtonText={t('buttons.btn_close')}
                onSubmit={() => {
                    setShowError(false);
                }}>
                <div className="">
                    <p>{t('pages.resources.errorBody')}</p>
                </div>
            </Modal>

            <div className={`${COMPONENT_CLASS}__header`}>
                <h1>{t('pages.resources.title')}</h1>
            </div>

            <div className="flex flex-wrap gap-5">
                <div className={`${COMPONENT_CLASS}__card cursor-pointer`} onClick={sendRequestToFMS}>
                    <div className={`${COMPONENT_CLASS}__image-wrapper`}>
                        <img src={symmioAcademyImg} alt="Symmio Academy" />
                    </div>
                    <div className="p-8">
                        <div className="font-bold text-xs uppercase mb-2">
                            {t('pages.resources.symmioAcademy')}
                        </div>
                        <div className="font-light text-sm leading-6">
                            {t('pages.resources.symmioAcademy_desc')}
                        </div>
                    </div>
                </div>

                <a className="max-w-full" href="https://app.brandyhq.com/symmio" target="_blank" rel="noreferrer">
                    <div className={`${COMPONENT_CLASS}__card`}>
                        <div className={`${COMPONENT_CLASS}__image-wrapper`}>
                            <img src={marketingMaterialsImg} alt="Marketing Materials" />
                        </div>
                        <div className="p-8">
                            <div className="font-bold text-xs uppercase mb-2">
                                {t('pages.resources.marketing')}
                            </div>
                            <div className="font-light text-sm leading-6">
                                {t('pages.resources.marketing_desc')}
                            </div>
                        </div>
                    </div>
                </a>

                <a className="max-w-full" href="https://help.symmio.com/en/" target="_blank" rel="noreferrer">
                    <div className={`${COMPONENT_CLASS}__card`}>
                        <div className={`${COMPONENT_CLASS}__image-wrapper`}>
                            <img src={helpCenterImg} alt={t('pages.resources.helpCenter')} />
                        </div>
                        <div className="p-8">
                            <div className="font-bold text-xs uppercase mb-2">
                                {t('pages.resources.helpCenter')}
                            </div>
                            <div className="font-light text-sm leading-6">
                                {t('pages.resources.helpCenter_desc')}
                            </div>
                        </div>
                    </div>
                </a>
            </div>

        </div>
    );
}

export default ResourcesPage;
